import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartLineUp, Users } from "@phosphor-icons/react";
import AppShell from "../../../components/layout/AppShell";
import { api, formatApiError } from "../../../lib/api";
import { FiltersBar } from "./FiltersBar";
import { SummaryCards } from "./SummaryCards";
import { TrendCharts } from "./TrendCharts";
import { SortableTable } from "./SortableTable";
import { CustomersTab } from "./CustomersTab";
import { CustomerStatement } from "./CustomerStatement";
import { ExportMenu } from "./ExportMenu";
import { periodColumns, customerColumns } from "./columns";
import { brandedPdf, plainPdf } from "./pdf";
import { presetRange, toParams, inr, num } from "./iaUtils";

const initial = () => { const [from, to] = presetRange("this_month"); return { preset: "this_month", from, to, source: "all" }; };
const CARD_PDF = [["sr_created", "SRs created", num], ["sr_completed", "SRs completed", num], ["inv_generated", "Invoices", num],
  ["inv_paid", "Invoices paid", num], ["billed", "Billed", inr], ["collected", "Collected", inr], ["outstanding", "Outstanding", inr],
  ["prepaid", "Prepaid", inr], ["health_created", "Health bookings", num], ["health_collected", "Health collected", inr],
  ["avg_invoice", "Avg invoice", inr], ["completion_rate", "Completion %", (v) => `${v}%`]];

export default function InvoiceAnalytics() {
  const [f, setF] = useState(initial);
  const [group, setGroup] = useState("day");
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [cust, setCust] = useState(null);
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!f.from || !f.to || f.from > f.to) return;
    setLoading(true);
    const params = toParams(f);
    Promise.all([
      api.get("/finance/invoice-analytics/overview", { params: { ...params, group } }),
      api.get("/finance/invoice-analytics/customers", { params }),
    ]).then(([o, c]) => { setOv(o.data); setCust(c.data); })
      .catch(e => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [f, group]);

  const pCols = periodColumns(group);
  const onBranded = () => brandedPdf({
    f, chartsEl: document.getElementById("ia-charts"), periodCols: pCols, periods: ov.periods, totals: ov.totals,
    leaderboards: cust.leaderboards, ageing: cust.ageing,
    cards: CARD_PDF.map(([k, label, fmt]) => ({ label, value: fmt(ov.totals[k]) })),
  });
  const onPlain = (kind) => (kind === "periods"
    ? plainPdf({ title: "Invoice Analytics - Period table", columns: pCols, rows: ov.periods, foot: { ...ov.totals, period: "TOTAL" }, f })
    : plainPdf({ title: "Invoice Analytics - Customer table", columns: customerColumns, rows: cust.customers, f }));

  return (
    <AppShell title="Invoice Analytics" subtitle="Finance · SRs, invoices, collections & dues"
              actions={ov && cust && <ExportMenu f={f} group={group} onBranded={onBranded} onPlain={onPlain}/>}>
      <style>{`@keyframes iaRise{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}.ia-rise{animation:iaRise .35s ease-out both}`}</style>
      <div className="space-y-4" data-testid="invoice-analytics">
        <FiltersBar f={f} setF={setF} group={group} setGroup={setGroup} categories={ov?.categories}/>
        <div className="flex items-center gap-2 border-b border-[#E5E2D9]">
          {[["overview", "Overview", ChartLineUp], ["customers", "Customers", Users]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ia-tab-${k}`}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 inline-flex items-center gap-1.5 ${tab === k ? "border-[#D23247] text-[#D23247]" : "border-transparent text-[#5C5A55]"}`}>
              <I size={15}/>{l}
            </button>
          ))}
          {loading && <span className="ml-auto text-xs text-[#8A8780]" data-testid="ia-loading">Updating…</span>}
          {ov && !loading && <span className="ml-auto text-[11px] text-[#8A8780] font-mono">vs {ov.prev_from} → {ov.prev_to}</span>}
        </div>
        {!ov || !cust ? <div className="py-16 text-center text-sm text-[#8A8780]">Loading analytics…</div> : tab === "overview" ? (
          <div className="space-y-4">
            <SummaryCards totals={ov.totals} previous={ov.previous}/>
            <TrendCharts periods={ov.periods} split={ov.source_split} group={group}/>
            <SortableTable testid="ia-period-table" columns={pCols} rows={ov.periods} rowKey={r => r.period}
                           footer={{ ...ov.totals, period: "TOTAL" }}/>
          </div>
        ) : <CustomersTab data={cust} onPick={(r) => setPick(r.customer_id)}/>}
      </div>
      {pick && <CustomerStatement customerId={pick} onClose={() => setPick(null)}/>}
    </AppShell>
  );
}
